import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Factory, Globe, Save, Pencil } from "lucide-react";

const EMPTY = { name: "", sector: "", country: "", description: "" };

export default function MyOrganization() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data: org, isLoading } = useQuery({
    queryKey: ["my-organization"],
    queryFn: async () => (await api.get("/organizations/current")).data,
  });

  useEffect(() => {
    if (org) {
      setForm({
        name: org.name,
        sector: org.attributes?.sector || "",
        country: org.attributes?.country || "",
        description: org.attributes?.description || "",
      });
    }
  }, [org]);

  const save = useMutation({
    mutationFn: async () => (await api.put(`/organizations/${org.id}`, form)).data,
    onSuccess: () => {
      toast.success("Organisation mise à jour");
      setEdit(false);
      qc.invalidateQueries({ queryKey: ["my-organization"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const writable = can("org:write");

  return (
    <AppShell title="Mon organisation" subtitle="Tenant courant — aucune création d'organisation depuis cet écran"
      actions={writable && org && !edit && (
        <Button size="sm" className="rounded-sm gap-2" onClick={() => setEdit(true)} data-testid="edit-my-org-button">
          <Pencil className="h-4 w-4" /> Modifier
        </Button>
      )}>
      {isLoading ? (
        <p className="text-sm text-muted-foreground font-mono">Chargement…</p>
      ) : !org ? (
        <p className="text-sm text-muted-foreground">Organisation du tenant introuvable.</p>
      ) : (
        <div className="max-w-2xl border border-border bg-card rounded-sm p-8" data-testid="my-org-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-sm bg-secondary flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground font-mono">org_id : {org.id}</div>
              <h2 className="font-head text-2xl font-bold" data-testid="my-org-name">{org.name}</h2>
            </div>
          </div>
          {!edit ? (
            <div className="space-y-3 text-sm">
              {org.attributes?.sector && <div className="flex items-center gap-2 text-muted-foreground"><Factory className="h-4 w-4" /> {org.attributes.sector}</div>}
              {org.attributes?.country && <div className="flex items-center gap-2 text-muted-foreground"><Globe className="h-4 w-4" /> {org.attributes.country}</div>}
              {org.attributes?.description && <p className="text-muted-foreground leading-relaxed pt-2">{org.attributes.description}</p>}
            </div>
          ) : (
            <div className="space-y-4">
              <div><Label className="text-xs">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="my-org-name-input" /></div>
              <div><Label className="text-xs">Secteur</Label><Input value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Pays</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={3} /></div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" className="rounded-sm" onClick={() => setEdit(false)}>Annuler</Button>
                <Button className="rounded-sm gap-2" onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="save-my-org-button">
                  <Save className="h-4 w-4" /> Enregistrer
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
